import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ORDER_STATUS_NAME_BY_SLUG = {
  nowe: 'Nowe',
  w_realizacji: 'W realizacji',
  zrealizowane: 'Zrealizowane',
  rozliczone: 'Rozliczone',
  anulowane: 'Anulowane',
  kontynuacja: 'Kontynuacja',
};

const STATUS_ACTION_META = {
  view: { label: 'Podgląd', description: 'Pozwala wyświetlać zlecenia z tym statusem.' },
  edit: { label: 'Edycja', description: 'Pozwala edytować zlecenia z tym statusem.' },
  delete: { label: 'Usuwanie', description: 'Pozwala usuwać zlecenia z tym statusem.' },
};

const PERMISSION_META = {
  clients_view_all: { group: 'Klienci', label: 'Klienci: podgląd wszystkich', description: 'Dostęp do wszystkich klientów.' },
  clients_view_own: { group: 'Klienci', label: 'Klienci: podgląd własnych', description: 'Dostęp tylko do własnych klientów.' },
  clients_create: { group: 'Klienci', label: 'Klienci: dodawanie', description: 'Możliwość dodawania klientów.' },
  clients_edit_all: { group: 'Klienci', label: 'Klienci: edycja wszystkich', description: 'Edycja wszystkich klientów.' },
  clients_edit_own: { group: 'Klienci', label: 'Klienci: edycja własnych', description: 'Edycja tylko własnych klientów.' },
  clients_delete_all: { group: 'Klienci', label: 'Klienci: usuwanie', description: 'Usuwanie klientów.' },
  orders_view_all: { group: 'Zlecenia', label: 'Zlecenia: podgląd wszystkich', description: 'Dostęp do wszystkich zleceń.' },
  orders_view_own: { group: 'Zlecenia', label: 'Zlecenia: podgląd własnych', description: 'Dostęp tylko do własnych zleceń.' },
  orders_create: { group: 'Zlecenia', label: 'Zlecenia: dodawanie', description: 'Tworzenie nowych zleceń.' },
  orders_edit_all: { group: 'Zlecenia', label: 'Zlecenia: edycja wszystkich', description: 'Edycja wszystkich zleceń.' },
  orders_edit_own: { group: 'Zlecenia', label: 'Zlecenia: edycja własnych', description: 'Edycja własnych zleceń.' },
  orders_delete_all: { group: 'Zlecenia', label: 'Zlecenia: usuwanie', description: 'Usuwanie zleceń.' },
  orders_continue: { group: 'Zlecenia', label: 'Zlecenia: kontynuacja', description: 'Możliwość tworzenia kontynuacji zleceń.' },
  users_view: { group: 'Użytkownicy i pracownicy', label: 'Podgląd pracowników', description: 'Wyświetlanie listy pracowników.' },
  users_manage: { group: 'Użytkownicy i pracownicy', label: 'Zarządzanie użytkownikami', description: 'Dodawanie, reset i edycja kont użytkowników.' },
  users_permissions_manage: { group: 'Użytkownicy i pracownicy', label: 'Zarządzanie RBAC', description: 'Edycja ról i uprawnień.' },
  files_manage: { group: 'Pliki', label: 'Zarządzanie plikami', description: 'Dodawanie i usuwanie plików.' },
  import_manage: { group: 'Import / Eksport', label: 'Import i eksport', description: 'Obsługa importu oraz eksportu danych.' },
  settings_manage: { group: 'Ustawienia', label: 'Zarządzanie ustawieniami', description: 'Dostęp do sekcji ustawień aplikacji.' },
  warehouse_view: { group: 'Magazyn', label: 'Magazyn: podgląd', description: 'Podgląd danych magazynowych.' },
  warehouse_manage: { group: 'Magazyn', label: 'Magazyn: zarządzanie (pełne)', description: 'Pełne zarządzanie magazynem.' },
  warehouse_items_view: { group: 'Magazyn', label: 'Magazyn: podgląd elementów', description: 'Wyświetlanie listy towarów/usług.' },
  warehouse_items_create: { group: 'Magazyn', label: 'Magazyn: dodawanie elementów', description: 'Dodawanie nowych towarów/usług.' },
  warehouse_items_edit: { group: 'Magazyn', label: 'Magazyn: edycja elementów', description: 'Edycja istniejących towarów/usług.' },
  warehouse_items_delete: { group: 'Magazyn', label: 'Magazyn: usuwanie elementów', description: 'Usuwanie/archiwizacja towarów/usług.' },
  hide_costs_margin: { group: 'Widoczność finansów', label: 'Ukryj koszty i marże', description: 'Ukrywa koszty i marże towarów w zleceniach i magazynie.' },
  hide_sale_prices: { group: 'Widoczność finansów', label: 'Ukryj ceny sprzedaży', description: 'Ukrywa ceny sprzedaży towarów w zleceniach i magazynie.' },
  page_clients_view: { group: 'Widok podstron', label: 'Podstrona: Klienci', description: 'Pokazuje pozycję „Klienci” w nawigacji i pozwala wejść na stronę.' },
  page_orders_view: { group: 'Widok podstron', label: 'Podstrona: Zlecenia', description: 'Pokazuje pozycję „Zlecenia” w nawigacji i pozwala wejść na stronę.' },
  page_employees_view: { group: 'Widok podstron', label: 'Podstrona: Pracownicy', description: 'Pokazuje pozycję „Pracownicy” w nawigacji i pozwala wejść na stronę.' },
  page_reviews_view: { group: 'Widok podstron', label: 'Podstrona: Przeglądy', description: 'Pokazuje pozycję „Przeglądy” w nawigacji i pozwala wejść na stronę.' },
  page_import_export_view: { group: 'Widok podstron', label: 'Podstrona: Import/Eksport', description: 'Pokazuje pozycję „Import/Eksport” w nawigacji i pozwala wejść na stronę.' },
  page_devices_view: { group: 'Widok podstron', label: 'Podstrona: Lista Urządzeń', description: 'Pokazuje pozycję „Lista Urządzeń” w nawigacji i pozwala wejść na stronę.' },
  page_users_view: { group: 'Widok podstron', label: 'Podstrona: Użytkownicy', description: 'Pokazuje pozycję „Użytkownicy” w nawigacji i pozwala wejść na stronę.' },
  page_warehouse_view: { group: 'Widok podstron', label: 'Podstrona: Magazyn', description: 'Pokazuje pozycję „Magazyn” w nawigacji i pozwala wejść na stronę.' },
  page_settings_view: { group: 'Widok podstron', label: 'Podstrona: Ustawienia', description: 'Pokazuje pozycję „Ustawienia” w nawigacji i pozwala wejść na stronę.' },
};

function prettifySlug(slug) {
  return String(slug || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getPermissionMeta(slug) {
  if (PERMISSION_META[slug]) {
    return PERMISSION_META[slug];
  }

  const statusMatch = String(slug || '').match(/^orders_status_([a-z0-9_]+)_(view|edit|delete)$/);
  if (statusMatch) {
    const [, statusSlug, action] = statusMatch;
    const statusName = ORDER_STATUS_NAME_BY_SLUG[statusSlug] || prettifySlug(statusSlug);
    const actionMeta = STATUS_ACTION_META[action] || { label: action, description: '' };
    return {
      group: 'Statusy zleceń',
      label: `Status „${statusName}”: ${actionMeta.label}`,
      description: actionMeta.description,
    };
  }

  return {
    group: 'Pozostałe',
    label: prettifySlug(slug),
    description: `Uprawnienie systemowe: ${slug}`,
  };
}

function getDetailedDescription(permission) {
  const slug = permission?.slug || '';
  const base = permission?.meta?.description || '';

  if (slug === 'orders_view_own') {
    return `${base} Własne zlecenia oznaczają te, gdzie użytkownik jest przypisany jako pracownik w tabeli zleceniapracownicy.`;
  }
  if (slug === 'orders_edit_own') {
    return `${base} Edycja dotyczy wyłącznie zleceń przypisanych użytkownikowi w tabeli zleceniapracownicy.`;
  }
  if (slug === 'orders_view_all' || slug === 'orders_edit_all') {
    return `${base} Dla wariantu „wszystkie” użytkownik nie musi być przypisany do zlecenia w zleceniapracownicy.`;
  }
  if (slug === 'import_manage') {
    return `${base} To uprawnienie dotyczy tylko modułu importu/eksportu i nie powinno wpływać na widoczność zleceń.`;
  }
  if (slug.startsWith('orders_status_')) {
    return `${base} Uprawnienie statusowe działa dodatkowo do uprawnień „własne”/„wszystkie” i zawęża operację do konkretnego statusu.`;
  }
  if (slug.startsWith('page_')) {
    return `${base} To uprawnienie steruje menu i wejściem na podstronę, ale nie nadaje automatycznie praw do operacji na danych.`;
  }
  if (slug.startsWith('warehouse_items_')) {
    return `${base} Dotyczy wyłącznie kartotek magazynowych (towary/usługi), bez wpływu na inne moduły.`;
  }

  return base;
}

export default function RBACPanel() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [dependencyRules, setDependencyRules] = useState({});
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [initialPermissionIds, setInitialPermissionIds] = useState([]);
  const [effectivePreview, setEffectivePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [status, setStatus] = useState('');
  const [debugMessages, setDebugMessages] = useState([]);
  const [hoveredPermissionId, setHoveredPermissionId] = useState(null);

  const token = localStorage.getItem('token');

  const pushDebug = (message, details) => {
    const timestamp = new Date().toISOString();
    const payload = details !== undefined ? ` ${JSON.stringify(details)}` : '';
    const line = `${timestamp} | ${message}${payload}`;
    setDebugMessages((prev) => [line, ...prev].slice(0, 50));
    console.log('[RBAC DEBUG]', message, details || '');
  };

  useEffect(() => {
    const load = async () => {
      const catalogUrl = `${API_URL}/uzytkownicy/rbac/catalog`;
      const rolesUrl = `${API_URL}/uzytkownicy/rbac/roles`;
      pushDebug('START_LOAD', {
        apiUrl: API_URL,
        origin: window.location.origin,
        hasToken: Boolean(token),
        catalogUrl,
        rolesUrl,
      });

      try {
        const [catalogRes, rolesRes] = await Promise.all([
          axios.get(catalogUrl, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(rolesUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        pushDebug('LOAD_OK', {
          catalogStatus: catalogRes.status,
          rolesStatus: rolesRes.status,
          backendInstance: catalogRes.headers?.['x-crm-backend-instance'] || rolesRes.headers?.['x-crm-backend-instance'] || null,
          traceCatalog: catalogRes.headers?.['x-crm-trace-id'] || null,
          traceRoles: rolesRes.headers?.['x-crm-trace-id'] || null,
        });

        setRoles(rolesRes.data || catalogRes.data?.roles || []);
        setPermissions(catalogRes.data?.permissions || []);
        setDependencyRules(catalogRes.data?.dependencyRules || {});
      } catch (err) {
        pushDebug('LOAD_ERROR', {
          status: err?.response?.status || null,
          error: err?.response?.data?.error || err?.message,
          url: err?.config?.url || null,
          method: err?.config?.method || null,
          backendInstance: err?.response?.headers?.['x-crm-backend-instance'] || null,
          traceId: err?.response?.headers?.['x-crm-trace-id'] || null,
        });
        setStatus(err?.response?.data?.error || 'Błąd ładowania danych RBAC');
      }
    };

    load();
  }, [token]);

  useEffect(() => {
    const loadRolePermissions = async () => {
      if (!selectedRoleId) {
        setSelectedPermissionIds([]);
        return;
      }

      try {
        const rolePermsUrl = `${API_URL}/uzytkownicy/rbac/roles/${selectedRoleId}/permissions`;
        pushDebug('LOAD_ROLE_PERMS_START', { rolePermsUrl, selectedRoleId });

        const res = await axios.get(rolePermsUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        pushDebug('LOAD_ROLE_PERMS_OK', {
          status: res.status,
          traceId: res.headers?.['x-crm-trace-id'] || null,
          backendInstance: res.headers?.['x-crm-backend-instance'] || null,
          selectedRoleId,
        });
        const loadedIds = (res.data?.permissions || []).map((permission) => permission.id);
        setSelectedPermissionIds(loadedIds);
        setInitialPermissionIds(loadedIds);
        setStatus('');
      } catch (err) {
        pushDebug('LOAD_ROLE_PERMS_ERROR', {
          status: err?.response?.status || null,
          error: err?.response?.data?.error || err?.message,
          url: err?.config?.url || null,
          traceId: err?.response?.headers?.['x-crm-trace-id'] || null,
          backendInstance: err?.response?.headers?.['x-crm-backend-instance'] || null,
          selectedRoleId,
        });
        setStatus(err?.response?.data?.error || 'Nie udało się pobrać uprawnień roli');
      }
    };

    loadRolePermissions();
  }, [selectedRoleId, token]);

  useEffect(() => {
    if (!selectedRoleId) {
      setEffectivePreview(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const previewUrl = `${API_URL}/uzytkownicy/rbac/effective-access/preview`;
        const res = await axios.post(
          previewUrl,
          { permissionIds: selectedPermissionIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEffectivePreview(res.data || null);
      } catch (err) {
        pushDebug('PREVIEW_ERROR', {
          status: err?.response?.status || null,
          error: err?.response?.data?.error || err?.message,
          url: err?.config?.url || null,
        });
      } finally {
        setPreviewLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [selectedRoleId, selectedPermissionIds, token]);

  const selectedRole = useMemo(
    () => roles.find((role) => String(role.id) === String(selectedRoleId)),
    [roles, selectedRoleId]
  );

  const autoAddedSet = useMemo(() => {
    const set = new Set();
    for (const item of effectivePreview?.autoAddedPermissions || []) {
      if (item?.permission) {
        set.add(item.permission);
      }
    }
    return set;
  }, [effectivePreview]);

  const permissionBySlug = useMemo(() => {
    const map = new Map();
    for (const permission of permissions) {
      map.set(permission.slug, { ...permission, meta: getPermissionMeta(permission.slug) });
    }
    return map;
  }, [permissions]);

  const selectedCount = selectedPermissionIds.length;

  const prettifyRoleName = (name) => {
    if (!name) return '';
    if (name === 'administrator') return 'Administrator';
    if (name === 'manager') return 'Manager';
    if (name === 'koordynator') return 'Koordynator';
    if (name === 'technik') return 'Technik';
    if (name === 'pomocnik') return 'Pomocnik';
    return prettifySlug(name);
  };

  const dirty = useMemo(() => {
    const a = [...selectedPermissionIds].sort((x, y) => x - y);
    const b = [...initialPermissionIds].sort((x, y) => x - y);
    if (a.length !== b.length) return true;
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return true;
    }
    return false;
  }, [selectedPermissionIds, initialPermissionIds]);

  const formatPermissionLabel = (slug) => {
    const item = permissionBySlug.get(slug);
    if (item?.meta?.label) return item.meta.label;
    return prettifySlug(slug);
  };

  const formatPermissionList = (slugs = []) => slugs.map((slug) => formatPermissionLabel(slug)).join(', ');

  const toggleAllVisible = (checked) => {
    const visibleIds = groupedPermissions
      .flatMap(([, groupItems]) => groupItems)
      .map((permission) => permission.id);

    setSelectedPermissionIds((prev) => {
      const set = new Set(prev);
      for (const permissionId of visibleIds) {
        if (checked) set.add(permissionId);
        else set.delete(permissionId);
      }
      return Array.from(set);
    });
  };

  const restoreFromLoaded = () => {
    setSelectedPermissionIds(initialPermissionIds);
    setStatus('Przywrócono ostatnio zapisany zestaw uprawnień dla tej roli.');
  };

  const toggleGroupExpanded = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !(prev[groupName] ?? true),
    }));
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const savePermissions = async () => {
    if (!selectedRoleId) {
      setStatus('Wybierz rolę.');
      return;
    }

    try {
      const saveUrl = `${API_URL}/uzytkownicy/rbac/roles/${selectedRoleId}/permissions`;
      pushDebug('SAVE_START', { saveUrl, selectedRoleId, permissionCount: selectedPermissionIds.length });

      const res = await axios.put(
        saveUrl,
        { permissionIds: selectedPermissionIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      pushDebug('SAVE_OK', { selectedRoleId });
      const savedIds = (res.data?.permissions || []).map((permission) => permission.id);
      setSelectedPermissionIds(savedIds);
      setInitialPermissionIds(savedIds);
      setEffectivePreview(res.data?.effectiveAccess || null);
      setStatus('Zapisano uprawnienia roli. Zmiany dla użytkowników tej roli będą aktywne po ponownym zalogowaniu.');
    } catch (err) {
      const details = err?.response?.data?.details || null;
      pushDebug('SAVE_ERROR', {
        status: err?.response?.status || null,
        error: err?.response?.data?.error || err?.message,
        url: err?.config?.url || null,
        traceId: err?.response?.headers?.['x-crm-trace-id'] || null,
        backendInstance: err?.response?.headers?.['x-crm-backend-instance'] || null,
        details,
      });
      const conflictText = (details?.conflicts || [])
        .map((conflict) => `${formatPermissionLabel(conflict.a)} ↔ ${formatPermissionLabel(conflict.b)}`)
        .join(', ');
      setStatus(
        conflictText
          ? `${err?.response?.data?.error || 'Nie udało się zapisać uprawnień roli.'} Konflikty: ${conflictText}`
          : err?.response?.data?.error || 'Nie udało się zapisać uprawnień roli.'
      );
    }
  };

  const toggleGroup = (groupItems, checked) => {
    const ids = groupItems.map((permission) => permission.id);
    setSelectedPermissionIds((prev) => {
      const set = new Set(prev);
      for (const permissionId of ids) {
        if (checked) set.add(permissionId);
        else set.delete(permissionId);
      }
      return Array.from(set);
    });
  };

  const groupedPermissions = useMemo(() => {
    const map = new Map();
    const normalizedSearch = searchText.trim().toLowerCase();

    for (const permission of permissions) {
      const meta = getPermissionMeta(permission.slug);

      const searchable = `${permission.slug} ${meta.label} ${meta.description}`.toLowerCase();
      if (normalizedSearch && !searchable.includes(normalizedSearch)) {
        continue;
      }

      if (showOnlySelected && !selectedPermissionIds.includes(permission.id)) {
        continue;
      }

      const group = meta.group || 'Pozostałe';
      if (!map.has(group)) map.set(group, []);
      map.get(group).push({ ...permission, meta });
    }
    return Array.from(map.entries())
      .map(([groupName, groupItems]) => [
        groupName,
        groupItems.sort((a, b) => a.meta.label.localeCompare(b.meta.label, 'pl')),
      ])
        .sort((a, b) => a[0].localeCompare(b[0], 'pl'));
      }, [permissions, searchText, showOnlySelected, selectedPermissionIds]);

  const filteredPermissionsCount = groupedPermissions.reduce((sum, [, items]) => sum + items.length, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 12, background: '#fff' }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Uprawnienia ról (RBAC)</h3>
        <p style={{ marginTop: 0, marginBottom: 10, color: '#4b5563', fontSize: 13 }}>
          Krok 1: wybierz rolę • Krok 2: ustaw uprawnienia • Krok 3: sprawdź podgląd i zapisz.
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
            <option value="">— wybierz rolę —</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {prettifyRoleName(role.name)}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Filtruj uprawnienia (np. zlecenia, magazyn, podstrona)"
            style={{ minWidth: 300, maxWidth: 520, flex: 1 }}
            disabled={!selectedRoleId}
          />

          <button type="button" className="add-button" onClick={savePermissions} disabled={!selectedRoleId || !dirty}>
            Zapisz uprawnienia
          </button>
        </div>

        {selectedRole && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#374151', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>Edytowana rola: <strong>{prettifyRoleName(selectedRole.name)}</strong></span>
            <span>Zaznaczone: <strong>{selectedCount}</strong></span>
            <span>Widoczne po filtrze: <strong>{filteredPermissionsCount}</strong></span>
            {dirty ? <span style={{ color: '#92400e' }}>Masz niezapisane zmiany</span> : <span style={{ color: '#065f46' }}>Zmiany zapisane</span>}
          </div>
        )}

        {selectedRoleId && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={() => toggleAllVisible(true)}>Zaznacz wszystko z widoku</button>
            <button type="button" onClick={() => toggleAllVisible(false)}>Odznacz wszystko z widoku</button>
            <button type="button" onClick={restoreFromLoaded} disabled={!dirty}>Przywróć ostatni zapis</button>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8, fontSize: 13, color: '#4b5563' }}>
              <input
                type="checkbox"
                checked={showOnlySelected}
                onChange={(e) => setShowOnlySelected(e.target.checked)}
              />
              Pokaż tylko zaznaczone
            </label>
          </div>
        )}
      </div>

      {selectedRole && (
        <div style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#f9fafb' }}>
          <strong>Podgląd uprawnień efektywnych</strong>
          {previewLoading && <div style={{ marginTop: 6, color: '#6b7280' }}>Aktualizuję podgląd zależności…</div>}
          {!previewLoading && effectivePreview && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div>Wybrane: {effectivePreview.requestedPermissions?.length || 0}</div>
              <div>Efektywne po regułach: {effectivePreview.effectivePermissions?.length || 0}</div>
              {(effectivePreview.autoAddedPermissions || []).length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <strong>Dodane automatycznie:</strong>{' '}
                  {formatPermissionList((effectivePreview.autoAddedPermissions || []).map((item) => item.permission))}
                </div>
              )}
              {(effectivePreview.warnings || []).length > 0 && (
                <div style={{ marginTop: 6, color: '#92400e' }}>
                  <strong>Ostrzeżenia:</strong>{' '}
                  {(effectivePreview.warnings || []).map((item) => item.message).join(' | ')}
                </div>
              )}
              {(effectivePreview.conflicts || []).length > 0 && (
                <div style={{ marginTop: 6, color: '#b91c1c' }}>
                  <strong>Konflikty:</strong>{' '}
                  {(effectivePreview.conflicts || []).map((item) => `${formatPermissionLabel(item.a)} ↔ ${formatPermissionLabel(item.b)}`).join(' | ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!selectedRoleId && (
        <div style={{ marginBottom: 12, padding: 12, border: '1px dashed #d1d5db', borderRadius: 8, color: '#6b7280' }}>
          Aby rozpocząć, wybierz rolę z listy powyżej.
        </div>
      )}

      {groupedPermissions.map(([groupName, groupItems]) => (
        <div key={groupName} style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => toggleGroupExpanded(groupName)} style={{ minWidth: 30 }}>
                {(expandedGroups[groupName] ?? true) ? '−' : '+'}
              </button>
              <strong style={{ display: 'block', textTransform: 'capitalize' }}>{groupName}</strong>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                zaznaczone: {groupItems.filter((item) => selectedPermissionIds.includes(item.id)).length}/{groupItems.length}
              </span>
              <button type="button" onClick={() => toggleGroup(groupItems, true)} disabled={!selectedRoleId}>Zaznacz grupę</button>
              <button type="button" onClick={() => toggleGroup(groupItems, false)} disabled={!selectedRoleId}>Odznacz grupę</button>
            </div>
          </div>

          {(expandedGroups[groupName] ?? true) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
            {groupItems.map((permission) => (
              <label
                key={permission.id}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: selectedPermissionIds.includes(permission.id) ? '#f9fafb' : '#fff' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissionIds.includes(permission.id)}
                    onChange={() => togglePermission(permission.id)}
                    style={{ marginTop: 3 }}
                    disabled={!selectedRoleId}
                  />
                  <span style={{ display: 'block' }}>
                    <span style={{ display: 'block', fontWeight: 600 }}>{permission.meta.label}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>{permission.meta.description}</span>
                  </span>
                </div>
                {autoAddedSet.has(permission.slug) && (
                  <div style={{ marginTop: 6, color: '#065f46', fontSize: 12 }}>Dodane automatycznie przez zależności</div>
                )}
                {Array.isArray(dependencyRules?.[permission.slug]?.requiresAll) && dependencyRules[permission.slug].requiresAll.length > 0 && (
                  <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                    Wymaga: {formatPermissionList(dependencyRules[permission.slug].requiresAll)}
                  </div>
                )}
                {Array.isArray(dependencyRules?.[permission.slug]?.requiresAnyOf) && dependencyRules[permission.slug].requiresAnyOf.length > 0 && (
                  <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
                    Wymaga jednego z: {dependencyRules[permission.slug].requiresAnyOf.map((group) => formatPermissionList(group)).join(' | ')}
                  </div>
                )}
                <span
                  style={{ marginTop: 6, color: '#6b7280', position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                  onMouseEnter={() => setHoveredPermissionId(permission.id)}
                  onMouseLeave={() => setHoveredPermissionId(null)}
                >
                  <span style={{ cursor: 'help', userSelect: 'none' }}>ⓘ</span>
                  {hoveredPermissionId === permission.id && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '140%',
                        left: 0,
                        zIndex: 30,
                        minWidth: 280,
                        maxWidth: 420,
                        background: '#111827',
                        color: '#f9fafb',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 12,
                        lineHeight: 1.45,
                        boxShadow: '0 8px 22px rgba(0,0,0,0.30)',
                        whiteSpace: 'normal',
                      }}
                    >
                      {getDetailedDescription(permission)}
                    </span>
                  )}
                </span>
              </label>
            ))}
            </div>
          )}
        </div>
      ))}

      {selectedRoleId && filteredPermissionsCount === 0 && (
        <div style={{ marginBottom: 12, padding: 12, border: '1px dashed #d1d5db', borderRadius: 8, color: '#6b7280' }}>
          Brak uprawnień pasujących do filtra. Zmień frazę wyszukiwania.
        </div>
      )}

      {status && (
        <p style={{ marginTop: 12, padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: status.toLowerCase().includes('nie udało') || status.toLowerCase().includes('konflikt') ? '#fef2f2' : '#f0fdf4' }}>
          {status}
        </p>
      )}

      <p style={{ marginTop: 8, color: '#6b7280', fontSize: 13 }}>
        Uwaga: po zmianie uprawnień roli użytkownik musi wylogować się i zalogować ponownie,
        aby token pobrał nowy zestaw uprawnień.
      </p>

      <details style={{ marginTop: 12 }}>
        <summary>Diagnostyka RBAC</summary>
        <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{debugMessages.join('\n') || 'Brak logów'}</pre>
      </details>
    </div>
  );
}
